import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { finalize, filter, pairwise } from 'rxjs';

import {
  FormControl,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { UserPreference } from './models/user-preference';
import {
  DrawerService,
  SpinnerService,
  ThemeService,
  TokenClaimsService,
  localStorageConstants,
} from 'src/app/shared/services';
import { UserPreferenceService } from 'src/app/shared/services/user-preference.service';
import {
  MatCardModule,
  MatDividerModule,
  MatFormFieldModule,
  MatInputModule,
  MatRadioModule,
  MatSnackBar,
} from 'src/app/shared/material';

@Component({
  selector: 'lib-user-settings',
  imports: [
    MatCardModule,
    MatRadioModule,
    MatInputModule,
    MatDividerModule,
    MatFormFieldModule,
    ReactiveFormsModule,
  ],
  templateUrl: './user-settings.component.html',
  styleUrl: './user-settings.component.scss',
})
export class UserSettingsComponent implements OnInit {
  userPreference: UserPreference;
  userPreferenceForm: FormGroup;
  theme: string = this.themeService.currentTheme;
  azureOId: string = '';

  formFields = {
    theme: 'theme',
  };

  private destroyRef = inject(DestroyRef);

  constructor(
    public themeService: ThemeService,
    private fb: FormBuilder,
    private userPreferenceService: UserPreferenceService,
    private claimsService: TokenClaimsService,
    private toaster: MatSnackBar,
    public settingsDrawer: DrawerService,
    private spinnerService: SpinnerService,
  ) {
    toObservable(this.settingsDrawer.state)
      .pipe(
        pairwise(),
        filter(([prev, curr]) => prev === true && curr === false),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.savePreferences());
  }

  ngOnInit(): void {
    this.userPreferenceForm = this.fb.group({
      theme: new FormControl({
        value: this.theme,
        disabled: false,
      }),
    });

    this.azureOId = this.claimsService.tokenClaims$.oid;
        this.setUserPreferences();
  }

  private savePreferences(): void {
    const formData = this.userPreferenceForm.value;
    const preference: UserPreference = {
      AzureOid: this.azureOId,
      Value: {
        theme: formData.theme,
      },
    };

    localStorage.setItem(
      localStorageConstants.USER_PREFERENCE_KEY,
      JSON.stringify(preference.Value),
    );
    this.themeService.loadUserTheme();

    this.spinnerService.show();
    this.userPreferenceService
      .save(this.azureOId, preference.Value)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (result) => {
          const update = result?.data?.UpdateUserPreference;
          if (!update?.Success) {
            this.toaster.open(update?.Message || 'Failed to save preferences.');
          }
        },
        error: () => {
          this.toaster.open('Failed to save preferences.');
        },
      });
  }

  setFormInput(inputName: string, value: any): void {
    this.userPreferenceForm.get(inputName).patchValue(value || null);
  }

  loadTheme(theme: string) {
    this.themeService.set(theme);
  }

  private setUserPreferences(): void {
    let userPreference = JSON.parse(
      localStorage.getItem(localStorageConstants.USER_PREFERENCE_KEY),
    );

    if (userPreference) {
      this.theme = userPreference.theme;

      this.setFormInput(this.formFields.theme, this.theme);
    }
  }
}
